import React, {
	useCallback, useMemo, useEffect,
} from 'react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { OmitFirstArg } from 'Util/TypeHelpers';
import { AbortSignals, useAbortSignals } from 'Hooks/useAbortSignals';

type WrappedFunc = (abortSignals: AbortSignals, ...params: any[]) => any;
type OuterFunc<W> = OmitFirstArg<W>;

type CallbackParam = <T extends WrappedFunc>(data: ReturnType<T>) => any;

/**
 * Takes a function and returns it as interruptable debounced
 * function
 *
 * debouncedFunc is debounced instance of provided function.
 * Interrupt is wrapped version of provided function that
 * cancels any pending debounced version of the provided
 * function. Execution of either function are ensured to be mutually
 * exclusive/
 *
 * @param func function to be debounced. Will be memoized
 * @param dependencies callback array of func dependcies. Length must
 * always be stable
 * @param wait lodash debounce wait value (milliseconds)
 * @param onSuccess function to be called after func resolved. Reference
 * should remain consistent
 * @param onError function to be called if the function throws an error.
 * Reference should remain consistent
 * @returns [debouncedFunc, interrupt]
 */
function useDebounce<T extends WrappedFunc>(
	func: T,
	dependencies: any[],
	wait: number = 300,
	onSuccess?: CallbackParam,
	onError?: CallbackParam,
) {
	const [
		createSignals, clearSignals, sendCancelSignals,
	] = useAbortSignals();

	const funcCallback = useCallback(
		async (...params: Parameters<OuterFunc<T>>): Promise<ReturnType<T>> => {
			const newAbortSignals = createSignals();
			return Promise.resolve(func(newAbortSignals, ...params));
		},
		/**
		 * disabled rule reasons: need to specify dynamic dependencies
		 * and exclude 'func', as it is updated when dependencies change
		 *
		 * if 'func' is included here, debouncing breaks
		 * whenever it is declared as an argument
		 */
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[...dependencies, createSignals],
	);

	const debouncedFunc = useMemo(
		() => AwesomeDebouncePromise(funcCallback, wait),
		[funcCallback, wait],
	);

	const interruptableDebouncedFunc = useCallback(
		(...params: Parameters<typeof debouncedFunc>) => {
			// cancel existing, start a new debounce queue
			sendCancelSignals();

			return debouncedFunc(...params)
				.then(resolvedData => {
					if (resolvedData) {
						onSuccess?.(resolvedData);
					}
					return resolvedData;
				})
				.catch(error => {
					onError?.(error);
					throw error;
				})
				.finally(() => {
					clearSignals();
				});
		},
		[sendCancelSignals, clearSignals, debouncedFunc, onError, onSuccess],
	);

	useEffect(() => {
		return () => {
			sendCancelSignals();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return interruptableDebouncedFunc;
}

export default useDebounce;
